import React from 'react';
import { Truck, AlertTriangle } from 'lucide-react';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { SupplierService } from 'services/catalog/supplier.service';
import { Card } from 'components/common/Card';
import { LoadingScreen } from 'components/common/Spinner';
import { formatCurrency } from 'utils/formatters';
import { Pagination } from 'components/common/Pagination';

const PAGE_SIZE = 8;

export default function SuppliersPage() {
  const { org } = useStore();
  const [page, setPage] = React.useState(1);
  const { data: suppliers, loading } = useApi(
    () => SupplierService.list(org?.id ?? '', page, PAGE_SIZE),
    [org?.id, page]
  );

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Truck size={20} className="text-gray-600" />
        <h1 className="text-xl font-bold text-gray-900">Suppliers</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(suppliers?.data ?? []).map(supplier => (
          <Card key={supplier.id}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{supplier.name}</h3>
                <p className="text-xs text-gray-400">{supplier.contact_name} · {supplier.phone}</p>
              </div>
              {(supplier.recent_cost_increases ?? 0) > 0 && (
                <div className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                  <AlertTriangle size={11} />
                  {supplier.recent_cost_increases} increases
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-gray-50 rounded-md p-2">
                <p className="text-xs text-gray-500">Products</p>
                <p className="text-lg font-bold text-gray-900">{supplier.product_count}</p>
              </div>
              <div className="bg-gray-50 rounded-md p-2">
                <p className="text-xs text-gray-500">Cost increases</p>
                <p className={`text-lg font-bold ${(supplier.recent_cost_increases ?? 0) > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                  {supplier.recent_cost_increases}
                </p>
              </div>
              <div className="bg-gray-50 rounded-md p-2">
                <p className="text-xs text-gray-500">Est. impact</p>
                <p className={`text-sm font-bold mt-1 ${(supplier.estimated_monthly_impact ?? 0) > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  {(supplier.estimated_monthly_impact ?? 0) > 0 ? `~${formatCurrency(supplier.estimated_monthly_impact!)}` : '—'}
                </p>
              </div>
            </div>

            {supplier.email && (
              <p className="mt-3 text-xs text-gray-400">{supplier.email}</p>
            )}
          </Card>
        ))}
      </div>
      {suppliers && <Pagination page={page} pageSize={PAGE_SIZE} total={suppliers.meta.total} onPageChange={setPage} />}
    </div>
  );
}

